var __accessCheck = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet = (obj, member, getter) => {
  __accessCheck(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateSet = (obj, member, value, setter) => {
  __accessCheck(obj, member, "write to private field");
  setter ? setter.call(obj, value) : member.set(obj, value);
  return value;
};
var _onQuery, _options;
import { Controller } from "stimulus";
import Combobox from "@github/combobox-nav";
import Pikaday from "pikaday";
import moment from "moment";
import { maskInput } from "vanilla-text-mask";
import { createNumberMask, createAutoCorrectedDatePipe } from "text-mask-addons";
import Popper from "popper.js";
import { createFocusTrap } from "focus-trap";
function debounce(func, delay) {
  let timerId;
  return (...args) => {
    clearTimeout(timerId);
    timerId = setTimeout(() => func(...args), delay);
  };
}
class AutosubmitController extends Controller {
  constructor() {
    super(...arguments);
    this.submit = () => {
      if (this.hasSubmitterTarget) {
        this.element.requestSubmit(this.submitterTarget);
      } else {
        this.element.requestSubmit();
      }
    };
    this.submitLater = debounce(this.submit, 500);
  }
}
AutosubmitController.targets = ["submitter"];
var __accessCheck$5 = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet$5 = (obj, member, getter) => {
  __accessCheck$5(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd$5 = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateSet$5 = (obj, member, value, setter) => {
  __accessCheck$5(obj, member, "write to private field");
  setter ? setter.call(obj, value) : member.set(obj, value);
  return value;
};
var _combobox, _onKeyDown$1, _onFocus, _onInput, _onBlur, _onMouseDown, _onCommit;
class AutocompleteController extends Controller {
  constructor() {
    super(...arguments);
    __privateAdd$5(this, _combobox, void 0);
    __privateAdd$5(this, _onKeyDown$1, (event) => {
      const isArrowKey = ["ArrowDown", "ArrowUp"].includes(event.key);
      if (isArrowKey && !this.openValue) {
        void this.query();
      }
    });
    __privateAdd$5(this, _onFocus, () => {
      this.searchTarget.select();
    });
    __privateAdd$5(this, _onInput, () => {
      this.valueTarget.value = "";
      void this.query();
    });
    __privateAdd$5(this, _onBlur, () => {
      this.close();
    });
    __privateAdd$5(this, _onMouseDown, (event) => {
      event.preventDefault();
    });
    __privateAdd$5(this, _onCommit, (event) => {
      const option = event.target;
      const text = option.textContent ?? "";
      this.searchTarget.value = option.dataset.autocompleteOptionLabel ?? text;
      this.valueTarget.value = option.dataset.autocompleteOptionValue ?? text;
      this.close();
    });
  }
  connect() {
    __privateSet$5(this, _combobox, new Combobox(this.searchTarget, this.optionsTarget, {
      tabInsertsSuggestions: true,
      firstOptionSelectionMode: "selected"
    }));
    this.searchTarget.addEventListener("focus", __privateGet$5(this, _onFocus));
    this.searchTarget.addEventListener("input", __privateGet$5(this, _onInput));
    this.searchTarget.addEventListener("blur", __privateGet$5(this, _onBlur));
    this.searchTarget.addEventListener("keydown", __privateGet$5(this, _onKeyDown$1));
    this.optionsTarget.addEventListener("mousedown", __privateGet$5(this, _onMouseDown));
    this.optionsTarget.addEventListener("combobox-commit", __privateGet$5(this, _onCommit));
  }
  disconnect() {
    var _a;
    this.element.classList.remove("is-open");
    this.searchTarget.removeEventListener("focus", __privateGet$5(this, _onFocus));
    this.searchTarget.removeEventListener("input", __privateGet$5(this, _onInput));
    this.searchTarget.removeEventListener("blur", __privateGet$5(this, _onBlur));
    this.searchTarget.removeEventListener("keydown", __privateGet$5(this, _onKeyDown$1));
    this.optionsTarget.removeEventListener("mousedown", __privateGet$5(this, _onMouseDown));
    this.optionsTarget.removeEventListener("combobox-commit", __privateGet$5(this, _onCommit));
    (_a = __privateGet$5(this, _combobox)) == null ? void 0 : _a.destroy();
    __privateSet$5(this, _combobox, void 0);
  }
  openValueChanged() {
    var _a, _b;
    if (this.openValue) {
      (_a = __privateGet$5(this, _combobox)) == null ? void 0 : _a.start();
      this.element.classList.add("is-open");
    } else {
      (_b = __privateGet$5(this, _combobox)) == null ? void 0 : _b.stop();
      this.element.classList.remove("is-open");
    }
  }
  /**
   * Public API
   */
  async query() {
    await new Promise((resume) => {
      const event = new CustomEvent("autocomplete-query", {
        bubbles: true,
        detail: { resume, query: this.searchTarget.value }
      });
      this.optionsTarget.dispatchEvent(event);
    });
    this.openValue = Boolean(this.optionsTarget.childElementCount);
    requestAnimationFrame(() => {
      var _a;
      return (_a = __privateGet$5(this, _combobox)) == null ? void 0 : _a.resetSelection();
    });
  }
  close() {
    this.openValue = false;
  }
}
_combobox = /* @__PURE__ */ new WeakMap();
_onKeyDown$1 = /* @__PURE__ */ new WeakMap();
_onFocus = /* @__PURE__ */ new WeakMap();
_onInput = /* @__PURE__ */ new WeakMap();
_onBlur = /* @__PURE__ */ new WeakMap();
_onMouseDown = /* @__PURE__ */ new WeakMap();
_onCommit = /* @__PURE__ */ new WeakMap();
AutocompleteController.targets = ["search", "value", "options"];
AutocompleteController.values = {
  open: { type: Boolean, default: false }
};
class AutocompleteOptionsController extends Controller {
  constructor() {
    super(...arguments);
    __privateAdd(this, _onQuery, (event) => {
      void Promise.resolve(this.query(event.detail.query)).finally(() => event.detail.resume());
    });
  }
  connect() {
    this.element.addEventListener("autocomplete-query", __privateGet(this, _onQuery));
  }
  disconnect() {
    this.element.removeEventListener("autocomplete-query", __privateGet(this, _onQuery));
    this.element.replaceChildren();
  }
}
_onQuery = new WeakMap();
var __accessCheck$4 = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet$4 = (obj, member, getter) => {
  __accessCheck$4(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd$4 = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateSet$4 = (obj, member, value, setter) => {
  __accessCheck$4(obj, member, "write to private field");
  setter ? setter.call(obj, value) : member.set(obj, value);
  return value;
};
var __privateMethod$1 = (obj, member, method) => {
  __accessCheck$4(obj, member, "access private method");
  return method;
};
var _abortController, _fetchOptions, fetchOptions_fn, _abort, abort_fn;
function isAbortError(error) {
  return error instanceof DOMException && error.name === "AbortError";
}
class AutocompleteFetchOptionsController extends AutocompleteOptionsController {
  constructor() {
    super(...arguments);
    __privateAdd$4(this, _fetchOptions);
    __privateAdd$4(this, _abort);
    __privateAdd$4(this, _abortController, new AbortController());
  }
  disconnect() {
    super.disconnect();
    __privateMethod$1(this, _abort, abort_fn).call(this);
  }
  async query(query) {
    __privateMethod$1(this, _abort, abort_fn).call(this);
    try {
      await __privateMethod$1(this, _fetchOptions, fetchOptions_fn).call(this, query);
    } catch (error) {
      if (!isAbortError(error)) {
        throw error;
      }
    }
  }
}
_abortController = /* @__PURE__ */ new WeakMap();
_fetchOptions = /* @__PURE__ */ new WeakSet();
fetchOptions_fn = async function(query) {
  const url = new URL(this.urlValue, window.location.href);
  url.searchParams.set("query", query);
  const response = await fetch(url, {
    signal: __privateGet$4(this, _abortController).signal,
    headers: { "Content-Type": "text/html" }
  });
  if (response.ok) {
    this.element.innerHTML = await response.text();
  } else {
    throw new Error(response.statusText);
  }
};
_abort = /* @__PURE__ */ new WeakSet();
abort_fn = function() {
  __privateGet$4(this, _abortController).abort();
  __privateSet$4(this, _abortController, new AbortController());
};
AutocompleteFetchOptionsController.values = {
  url: String
};
class AutocompleteInlineOptionsController extends AutocompleteOptionsController {
  constructor() {
    super(...arguments);
    __privateAdd(this, _options, []);
  }
  connect() {
    super.connect();
    __privateSet(this, _options, Array.from(this.element.children));
    this.element.replaceChildren();
  }
  disconnect() {
    super.disconnect();
    this.element.replaceChildren(...__privateGet(this, _options));
  }
  query(query) {
    const pattern = new RegExp(query, "i");
    const options = __privateGet(this, _options).filter((option) => {
      const label = option.dataset.comboboxLabel ?? option.textContent ?? "";
      return pattern.test(label);
    });
    this.element.replaceChildren(...options);
  }
}
_options = new WeakMap();
function assert(condition, message) {
  if (!condition)
    throw new Error(message);
}
var __accessCheck$3 = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet$3 = (obj, member, getter) => {
  __accessCheck$3(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd$3 = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateSet$3 = (obj, member, value, setter) => {
  __accessCheck$3(obj, member, "write to private field");
  setter ? setter.call(obj, value) : member.set(obj, value);
  return value;
};
var _datepicker;
class DatepickerController extends Controller {
  constructor() {
    super(...arguments);
    __privateAdd$3(this, _datepicker, void 0);
  }
  connect() {
    assert(this.formatValue, "data-datepicker-format-value is required");
    __privateSet$3(this, _datepicker, new Pikaday({
      field: this.element,
      format: this.formatValue,
      toString(date, format) {
        return moment(date).format(format);
      },
      parse(value, format) {
        const date = moment(value, format);
        return date.isValid() ? date.toDate() : null;
      },
      i18n: {
        previousMonth: "Previous Month",
        nextMonth: "Next Month",
        months: [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December"
        ],
        weekdays: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        weekdaysShort: ["S", "M", "T", "W", "T", "F", "S"]
      }
    }));
  }
  disconnect() {
    var _a;
    (_a = __privateGet$3(this, _datepicker)) == null ? void 0 : _a.destroy();
  }
}
_datepicker = /* @__PURE__ */ new WeakMap();
DatepickerController.values = {
  format: String
};
var __accessCheck$2 = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet$2 = (obj, member, getter) => {
  __accessCheck$2(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd$2 = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateSet$2 = (obj, member, value, setter) => {
  __accessCheck$2(obj, member, "write to private field");
  setter ? setter.call(obj, value) : member.set(obj, value);
  return value;
};
var _mask;
const MASK_CONFIG = {
  cents: {
    guide: false,
    mask: [/\d/, /\d/]
  },
  currency: {
    guide: false,
    mask: createNumberMask({ prefix: "", allowDecimal: false })
  },
  currencyWithCents: {
    guide: false,
    mask: createNumberMask({ prefix: "", allowDecimal: true })
  },
  date: {
    guide: false,
    mask: [/\d/, /\d/, "/", /\d/, /\d/, "/", /\d/, /\d/, /\d/, /\d/],
    pipe: createAutoCorrectedDatePipe("mm/dd/yyyy")
  },
  phoneNumber: {
    guide: false,
    mask: [/[1-9]/, /\d/, /\d/, "-", /\d/, /\d/, /\d/, "-", /\d/, /\d/, /\d/, /\d/]
  },
  socialSecurityNumber: {
    guide: false,
    mask: [/\d/, /\d/, /\d/, "-", /\d/, /\d/, "-", /\d/, /\d/, /\d/, /\d/]
  },
  zip: {
    guide: false,
    mask: [/\d/, /\d/, /\d/, /\d/, /\d/]
  }
};
const MASK_TYPES = Object.keys(MASK_CONFIG);
class MaskController extends Controller {
  constructor() {
    super(...arguments);
    __privateAdd$2(this, _mask, void 0);
  }
  connect() {
    assert(this.typeValue, "data-mask-type-value is required");
    const config = MASK_CONFIG[this.typeValue];
    assert(config, `data-mask-type-value must be one of ${MASK_TYPES.join(", ")}`);
    __privateSet$2(this, _mask, maskInput({ inputElement: this.element, ...config }));
  }
  disconnect() {
    var _a;
    (_a = __privateGet$2(this, _mask)) == null ? void 0 : _a.destroy();
  }
}
_mask = /* @__PURE__ */ new WeakMap();
MaskController.values = {
  type: { type: String, default: void 0 }
};
var __accessCheck$1 = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet$1 = (obj, member, getter) => {
  __accessCheck$1(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd$1 = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateSet$1 = (obj, member, value, setter) => {
  __accessCheck$1(obj, member, "write to private field");
  setter ? setter.call(obj, value) : member.set(obj, value);
  return value;
};
var __privateMethod = (obj, member, method) => {
  __accessCheck$1(obj, member, "access private method");
  return method;
};
var _popper, _onKeyDown, _onMouseLeave, _createPopper, createPopper_fn, _renderTemplate, renderTemplate_fn, _removePopper, removePopper_fn;
class TooltipController extends Controller {
  constructor() {
    super(...arguments);
    __privateAdd$1(this, _createPopper);
    __privateAdd$1(this, _renderTemplate);
    __privateAdd$1(this, _removePopper);
    __privateAdd$1(this, _popper, void 0);
    this.open = () => {
      this.openValue = true;
    };
    this.close = () => {
      this.openValue = false;
    };
    this.toggle = () => {
      this.openValue = !this.openValue;
    };
    __privateAdd$1(this, _onKeyDown, (event) => {
      if (event.key === "Escape") {
        this.openValue = false;
      }
    });
    __privateAdd$1(this, _onMouseLeave, (event) => {
      var _a;
      const currentHover = event.relatedTarget;
      const isHoveringElement = this.element.contains(currentHover);
      const isHoveringTooltip = (_a = __privateGet$1(this, _popper)) == null ? void 0 : _a.popper.contains(currentHover);
      if (!isHoveringElement && !isHoveringTooltip) {
        this.openValue = false;
      }
    });
  }
  connect() {
    this.element.addEventListener("focus", this.open);
    this.element.addEventListener("blur", this.close);
    this.element.addEventListener("mouseenter", this.open);
    this.element.addEventListener("mouseleave", __privateGet$1(this, _onMouseLeave));
    this.element.addEventListener("touchstart", this.toggle, { passive: true });
    document.addEventListener("keydown", __privateGet$1(this, _onKeyDown));
  }
  disconnect() {
    __privateMethod(this, _removePopper, removePopper_fn).call(this);
    this.element.removeEventListener("focus", this.open);
    this.element.removeEventListener("blur", this.close);
    this.element.removeEventListener("mouseenter", this.open);
    this.element.removeEventListener("mouseleave", __privateGet$1(this, _onMouseLeave));
    this.element.removeEventListener("touchstart", this.toggle);
    document.removeEventListener("keydown", __privateGet$1(this, _onKeyDown));
  }
  openValueChanged() {
    if (this.openValue) {
      __privateMethod(this, _createPopper, createPopper_fn).call(this);
    } else {
      __privateMethod(this, _removePopper, removePopper_fn).call(this);
    }
  }
}
_popper = /* @__PURE__ */ new WeakMap();
_onKeyDown = /* @__PURE__ */ new WeakMap();
_onMouseLeave = /* @__PURE__ */ new WeakMap();
_createPopper = /* @__PURE__ */ new WeakSet();
createPopper_fn = function() {
  __privateGet$1(this, _popper) ?? __privateSet$1(this, _popper, new Popper(this.element, __privateMethod(this, _renderTemplate, renderTemplate_fn).call(this), {
    placement: this.placementValue
  }));
  __privateGet$1(this, _popper).scheduleUpdate();
};
_renderTemplate = /* @__PURE__ */ new WeakSet();
renderTemplate_fn = function() {
  const template = this.templateTarget.content;
  assert(template.firstElementChild, "tooltip template must contain a root element");
  assert(template.childElementCount < 2, "tooltip template contains more than one root element");
  const tooltip = template.firstElementChild.cloneNode(true);
  tooltip.addEventListener("mouseleave", __privateGet$1(this, _onMouseLeave));
  document.body.appendChild(tooltip);
  return tooltip;
};
_removePopper = /* @__PURE__ */ new WeakSet();
removePopper_fn = function() {
  var _a, _b;
  (_a = __privateGet$1(this, _popper)) == null ? void 0 : _a.destroy();
  (_b = __privateGet$1(this, _popper)) == null ? void 0 : _b.popper.remove();
  __privateSet$1(this, _popper, void 0);
};
TooltipController.targets = ["template"];
TooltipController.values = {
  open: { type: Boolean, default: false },
  placement: { type: String, default: "top" }
};
var __accessCheck2 = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet2 = (obj, member, getter) => {
  __accessCheck2(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd2 = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateSet2 = (obj, member, value, setter) => {
  __accessCheck2(obj, member, "write to private field");
  setter ? setter.call(obj, value) : member.set(obj, value);
  return value;
};
var _focusTrap, _dialog, dialog_get, _onDeactivate, _onLegacyEvent;
class ModalController extends Controller {
  constructor() {
    super(...arguments);
    __privateAdd2(this, _dialog);
    __privateAdd2(this, _focusTrap, void 0);
    __privateAdd2(this, _onDeactivate, () => {
      this.close();
    });
    __privateAdd2(this, _onLegacyEvent, (event) => {
      const target = event.target;
      if (target == null ? void 0 : target.classList.contains("sc-Modal")) {
        event.stopPropagation();
      } else if (target == null ? void 0 : target.classList.contains("sc-Modal-close")) {
        event.stopPropagation();
      } else if (target == null ? void 0 : target.hasAttribute("data-sc-modal-close")) {
        event.stopPropagation();
        event.preventDefault();
        const isClick = event.type === "click";
        const isEnterOrSpaceKey = event instanceof KeyboardEvent && ["Enter", " "].includes(event.key);
        if (isClick || isEnterOrSpaceKey) {
          this.close();
        }
      }
    });
  }
  connect() {
    this.element.addEventListener("click", __privateGet2(this, _onLegacyEvent));
    this.element.addEventListener("keydown", __privateGet2(this, _onLegacyEvent));
  }
  disconnect() {
    var _a;
    this.element.removeEventListener("click", __privateGet2(this, _onLegacyEvent));
    this.element.removeEventListener("keydown", __privateGet2(this, _onLegacyEvent));
    (_a = __privateGet2(this, _focusTrap)) == null ? void 0 : _a.deactivate();
    __privateSet2(this, _focusTrap, void 0);
  }
  openValueChanged() {
    __privateGet2(this, _focusTrap) ?? __privateSet2(this, _focusTrap, createFocusTrap(__privateGet2(this, _dialog, dialog_get), {
      clickOutsideDeactivates: true,
      escapeDeactivates: true,
      onDeactivate: __privateGet2(this, _onDeactivate)
    }));
    if (this.openValue) {
      this.element.classList.add("is-open");
      __privateGet2(this, _focusTrap).activate();
    } else {
      this.element.classList.remove("is-open");
      __privateGet2(this, _focusTrap).deactivate();
    }
  }
  open() {
    this.openValue = true;
  }
  close() {
    this.openValue = false;
  }
}
_focusTrap = /* @__PURE__ */ new WeakMap();
_dialog = /* @__PURE__ */ new WeakSet();
dialog_get = function() {
  const dialog = this.element.querySelector("[role=dialog]");
  assert(dialog, 'data-controller="modal" must contain an element with role="dialog"');
  return dialog;
};
_onDeactivate = /* @__PURE__ */ new WeakMap();
_onLegacyEvent = /* @__PURE__ */ new WeakMap();
ModalController.values = {
  open: { type: Boolean, default: false }
};
class ModalTriggerController extends Controller {
  open() {
    this.modalOutlet.open();
  }
  confirmSubmit(event) {
    const form = event.target;
    const modal = this.modalOutlet;
    const isValidModal = form.contains(modal.element);
    assert(isValidModal, "Your confirmation modal must appear within the form");
    const isDisabled = event.submitter && this.disabledTargets.includes(event.submitter);
    if (isDisabled)
      return;
    const isConfirmed = modal.openValue && modal.element.contains(event.submitter);
    if (isConfirmed) {
      this.modalOutlet.close();
    } else {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.modalOutlet.open();
    }
  }
}
ModalTriggerController.outlets = ["modal"];
ModalTriggerController.targets = ["disabled"];
function registerSambaControllers(application) {
  application.register("autosubmit", AutosubmitController);
  application.register("autocomplete", AutocompleteController);
  application.register("autocomplete-inline-options", AutocompleteInlineOptionsController);
  application.register("autocomplete-fetch-options", AutocompleteFetchOptionsController);
  application.register("datepicker", DatepickerController);
  application.register("mask", MaskController);
  application.register("tooltip", TooltipController);
  application.register("modal", ModalController);
  application.register("modal-trigger", ModalTriggerController);
}
export {
  AutocompleteController,
  AutocompleteFetchOptionsController,
  AutocompleteInlineOptionsController,
  AutocompleteOptionsController,
  AutosubmitController,
  DatepickerController,
  MaskController,
  ModalController,
  ModalTriggerController,
  TooltipController,
  registerSambaControllers
};
//# sourceMappingURL=samba-stimulus.mjs.map
